<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :padding="false"
        size="md">
        <template v-slot:header>
            <div class="p-6">
                Media
            </div>
        </template>

        <div class="p-12 pt-10 flex flex-col gap-4">
            <vx-alert :closeable="false">
                <span v-if="props.blockImages && !props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: mp4, mp3, csv, pdf, vcard</span>
                <span v-else-if="!props.blockImages && props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: gif, jpg, jpeg, png, heic</span>
                <span v-else>Allowed media types:<br/> gif, jpg, jpeg, png, heic, csv, pdf, vcard, mp4, mp3</span>
            </vx-alert>
            <input type="file"
                id="media-input"
                class="absolute opacity-0"
                @change="fileChange">
            <label v-if="!url"
                data-test="media-drop"
                for="media-input"
                class="px-8 py-16 bg-slate-50 rounded-2xl border-dashed border-2 border-slate-300 flex-col justify-center items-center gap-2 flex cursor-pointer mb-0"
                :class="{
                    'border-sky-200 bg-white': dragging,
                }"
                @dragenter.prevent.stop="dragging = true"
                @dragover.prevent.stop="dragging = true"
                @dragleave.prevent.stop="dragging = false"
                @drop.prevent.stop="dropped">
                <font-awesome-icon widthAuto v-if="!uploading"
                    :icon="faPhotoFilmMusic"
                    class="text-sky-400 text-5xl">
                </font-awesome-icon>
                <vx-icon-loading v-else
                    class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                <div class="text-center text-balance text-slate-700 text-sm font-normal">
                    <template v-if="uploading">
                        Uploading...
                    </template>
                    <template v-else>
                        Drag or paste media here, or<br/> click to open file browser.
                    </template>
                </div>
            </label>
            <div v-if="url"
                class="w-96 max-w-full mx-auto">
                <message-builder-media-preview :mediaUrl="url"
                    :mediaContentType="contentType"
                    @remove="remove()"
                    removeable>
                </message-builder-media-preview>
            </div>
            <div class="text-center text-slate-500 text-sm">
                Limited to one attachment with 1000KiB file size.
                <template v-if="url">
                    <br />
                    Remove existing file to add a new one.
                </template>
            </div>
            <vx-alert v-if="error"
                :closeable="false"
                color="danger"
                class="whitespace-pre-line">{{ error }}</vx-alert>
            <vx-alert v-if="warning"
                color="info"
                :closeable="false">
                {{ warning }}
            </vx-alert>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click.prevent="emit('update:visible', false)"
                    color="muted"
                    size="lg"
                    type="button">
                    Cancel
                </vx-button>
                <vx-button @click.prevent="save()"
                    type="button"
                    color="primary"
                    size="lg"
                    class="grow"
                    data-test="save-btn"
                    :disabled="!url"
                    :loading="uploading">
                    Save
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxButton, VxModal, VxAlert, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { messageMediaClient } from '~/services';
import { faCircleCheck, faPhotoFilmMusic } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import MessageBuilderMediaPreview from './MessageBuilderMediaPreview.vue';
import { ImageResizeError, resizeImage } from './resizeImage';
import { ImageBlobReduce } from 'image-blob-reduce';
import { isHeic } from '~/utils/files';
import { ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPES } from '~/utils/message';
import { ValidationError } from '~/utils/errors';

const props = defineProps({
    visible: Boolean,
    mediaUrl: {
        type: String,
        required: false,
    },
    mediaContentType: {
        type: String,
        required: false,
    },
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'update:mediaUrl', 'update:mediaContentType']);

const dragging = ref(false);
const uploading = ref(false);
const teamId = Spark.state.currentTeam.id;

const error = ref('');
const warning = ref('');
const url = ref(undefined);
const contentType = ref(undefined);

const assertFileTypeAllowed = (type) => {
    if (
        (props.blockImages && !props.blockFiles && !ALLOWED_FILE_TYPES.includes(type)) ||
        (!props.blockImages && props.blockFiles && !ALLOWED_IMAGE_TYPES.includes(type))
    ) {
        throw new ValidationError('The file type is not supported.');
    }
}

const dropped = (event) => {
    if (event.dataTransfer.files[0]) {
        upload(event.dataTransfer.files[0]);
    }
    dragging.value = false;
}

const pasted = (event) => {
    const file = event.clipboardData?.files[0];

    if (event.defaultPrevented || uploading.value || url.value || !file) {
        return;
    }

    event.preventDefault();
    upload(file);
}

useEventListener(() => (props.visible ? window : null), 'paste', pasted);

const fileChange = (event) => {
    if (event.target.files[0]) {
        upload(event.target.files[0]);
        event.target.value = '';
    }
}

const imageDimensions = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                resolve([img.width, img.height]);
            }
            img.onerror = (error) => {
                reject(error)
            }
            img.src = event.target.result;
        }
        reader.onerror = (error) => {
            reject(error)
        }
        reader.readAsDataURL(file);
    })
}

const halveImageDimension = async (file) => {
    const [width] = await imageDimensions(file);

    const resizedWidth = width / 2;
    const reducer = new ImageBlobReduce()
    const result = await reducer.toBlob(file, { max: resizedWidth })

    const resizedFile = new File([result], file.name, {
        type: file.type,
    });

    return resizedFile;
}

const reduceImageQuality = async (file, quality) => {
    if (quality && (quality > 1 || quality < 0.1)) {
        throw new Error('reduceImageQuality(): quality should be between 0.1-1.0. Provided: ' + quality)
    }
    const reducer = new ImageBlobReduce()

    reducer._create_blob = function (env) {
        return this.pica.toBlob(env.out_canvas, file.type, quality)
            .then(function (blob) {
                env.out_blob = blob;
                return env;
            });
    };
    const result = await reducer.toBlob(file)

    return new File([result], file.name, {
        type: file.type,
    });
}

const imageEncoder = {
    reduceQuality: reduceImageQuality,
    halve: halveImageDimension,
};

class HeicConversionError extends Error {
    constructor(cause) {
        super('Could not convert the HEIC image to JPEG.', { cause });
    }
}

/**
 * @param {File} file
 */
const convertHeicToJpeg = async (file) => {
    // loaded on demand: the bundled libheif wasm is around 3MiB
    const { heicTo } = await import('heic-to');
    let blob;

    try {
        blob = await heicTo({
            blob: file,
            type: 'image/jpeg',
        });
    } catch (e) {
        throw new HeicConversionError(e);
    }

    return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpeg`, { type: 'image/jpeg' });
}

/**
 * Browsers without a HEIF codec report an empty or image/heif type for HEIC files.
 * @param {File} file
 */
const detectFileType = async (file) => {
    // an unreadable file is left for the upload request to reject
    if (await isHeic(file).catch(() => false)) {
        return 'image/heic';
    }

    return file.type;
}

const upload = async (file) => {
    if (uploading.value) {
        return;
    }
    try {
        uploading.value = true;
        error.value = '';
        const media = await uploadMedia(file);
        url.value = media.url;
        contentType.value = media.contentType;
        warning.value = media.warning;
    } catch (e) {
        error.value = e.message;
    } finally {
        uploading.value = false;
    }
}

const uploadFailureMessage = (e) => {
    if (e?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
        return e.response.data?.errors?.file?.[0] || 'The file failed to upload.';
    }
    if (e instanceof HeicConversionError) {
        return 'The HEIC image could not be converted to JPEG. Please upload it as a JPEG or PNG instead.';
    }
    if (e instanceof ImageResizeError) {
        return 'The image could not be resized to under 500KiB. Please upload a smaller image.';
    }
    if (e?.message?.toLowerCase?.()?.includes('fingerprinting protection')) {
        return 'It was not possible to resize the image due to fingerprinting protection. Upload an image smaller than 500KiB.';
    }

    return 'The file failed to upload. Please try again.';
}

const uploadMedia = async (file) => {
    const type = await detectFileType(file);
    assertFileTypeAllowed(type);

    const staticImage = ['image/jpeg', 'image/png'].includes(type);
    const heicImage = 'image/heic' === type;
    const biggerThan500KiB = file.size / 1024 > 500;
    const smallerThan1000KiB = file.size / 1024 <= 1000;

    if (!smallerThan1000KiB && !staticImage && !heicImage) {
        throw new ValidationError('Max file size is 1000KiB.');
    }

    if (file.size / 1024 / 1024 > 20) {
        throw new ValidationError('The source image must not be more than 20MiB.');
    }

    try {
        const formData = new FormData();
        let uploadedContentType = type;
        let uploadWarning = '';

        if (heicImage) {
            let image = await convertHeicToJpeg(file);
            uploadedContentType = image.type;
            uploadWarning = 'The image has been automatically converted from HEIC format to JPEG.';

            if (image.size / 1024 > 500) {
                image = await resizeImage(image, imageEncoder);
                uploadWarning = 'The image has been automatically converted from HEIC format to JPEG and resized to be under 500KiB.';
            }

            formData.append('file', image);
        } else if (biggerThan500KiB && staticImage) {
            const blob = await resizeImage(file, imageEncoder);
            formData.append('file', blob, file.name)

            uploadWarning = 'The image has been automatically resized to be under 500KiB.';
        } else {
            formData.append('file', file);

            if (biggerThan500KiB) {
                uploadWarning = 'The attachment exceeds 500KiB. Please note that it may fail to be delivered, and you may still be charged. We recommend replacing the attachment with something smaller than 500KiB in size to ensure successful delivery and avoid potential charges.';
            }
        }

        const response = await messageMediaClient.uploadMedia(teamId, formData)

        return {
            url: response.data.url,
            contentType: uploadedContentType,
            warning: uploadWarning,
        };
    } catch (e) {
        console.error(e);
        throw new ValidationError(uploadFailureMessage(e));
    }
}

const remove = () => {
    error.value = '';
    warning.value = '';
    url.value = undefined;
    contentType.value = undefined;
}

const save = async () => {
    if (uploading.value) {
        return;
    }

    emit('update:mediaContentType', contentType.value);
    await nextTick();
    emit('update:mediaUrl', url.value);
    emit('update:visible', false)
}

watch(() => props.visible, () => {
    uploading.value = false;
    dragging.value = false;
    error.value = '';
    warning.value = '';
    url.value = props.mediaUrl;
    contentType.value = props.mediaContentType;
});

defineExpose({
    uploadMedia,
});
</script>
